import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';
import { usePermissions } from '@/features/permissions';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Candidate = { document_id: string; revision_id: string; title: string };

export function ReferralSelection({ enabled }: { enabled: boolean }) {
  const { org, mutation, permitted } = useReferralSubmission();
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const candidates = useQuery({
    enabled: permitted,
    queryKey: ['agent-candidates', org, offset],
    queryFn: async () =>
      (
        await clientApi.get<{ items: Candidate[]; has_more: boolean }>(
          `/api/v1/organizations/${org}/agent/candidates?offset=${offset}`,
        )
      ).data,
  });
  if (!permitted) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Select existing faxes or uploads</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p>
          Choose up to 10 documents with complete saved text. Submission creates review proposals only. Documents
          without eligible extraction or access are omitted.
        </p>
        {candidates.isPending && <p role="status">Loading documents…</p>}
        {candidates.isError && (
          <p role="alert">
            Documents unavailable.{' '}
            <Button variant="outline" onClick={() => candidates.refetch()}>
              Retry
            </Button>
          </p>
        )}
        <FieldGroup>
          {candidates.data?.items.map((item) => (
            <Field key={item.revision_id} orientation="horizontal">
              <Checkbox
                id={`referral-${item.revision_id}`}
                checked={selected.includes(item.revision_id)}
                disabled={mutation.isPending || (!selected.includes(item.revision_id) && selected.length >= 10)}
                onCheckedChange={(checked) => {
                  mutation.reset();
                  setSelected((ids) =>
                    checked === true ? [...ids, item.revision_id] : ids.filter((id) => id !== item.revision_id),
                  );
                }}
              />
              <FieldLabel htmlFor={`referral-${item.revision_id}`}>{item.title}</FieldLabel>
            </Field>
          ))}
        </FieldGroup>
        {candidates.data && !candidates.data.items.length && <p>No eligible documents on this page.</p>}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={!offset || mutation.isPending}
            onClick={() => setOffset(Math.max(0, offset - 30))}>
            Previous documents
          </Button>
          <Button
            variant="outline"
            disabled={!candidates.data?.has_more || mutation.isPending}
            onClick={() => setOffset(offset + 30)}>
            Next documents
          </Button>
          <Button
            disabled={!enabled || !selected.length || mutation.isPending}
            onClick={() => mutation.mutate(selected, { onSuccess: () => setSelected([]) })}>
            {mutation.isPending ? 'Sending…' : `Send ${selected.length} selected to referral review`}
          </Button>
        </div>
        <SubmissionStatus failed={mutation.isError} succeeded={mutation.isSuccess} />
      </CardContent>
    </Card>
  );
}

export function SendToReferralReview({ ready, revisionId }: { revisionId: string; ready: boolean }) {
  const { mutation, permitted } = useReferralSubmission();
  if (!permitted) return null;
  return (
    <div className="flex flex-col items-start gap-2">
      <Button
        variant="outline"
        disabled={!ready || mutation.isPending || mutation.isSuccess}
        onClick={() => mutation.mutate([revisionId])}>
        {mutation.isPending ? 'Sending…' : 'Send to referral review'}
      </Button>
      <p className="text-sm text-muted-foreground">
        {ready
          ? 'Uses the saved extracted text; no additional OCR.'
          : 'Complete text extraction before referral review.'}
      </p>
      <SubmissionStatus failed={mutation.isError} succeeded={mutation.isSuccess} />
    </div>
  );
}

function SubmissionStatus({ failed, succeeded }: { failed: boolean; succeeded: boolean }) {
  return (
    <>
      {failed && (
        <p role="alert">
          Could not queue this selection. Processing may be disabled, the pilot limit reached, or a source changed. No
          part of this selection was queued.
        </p>
      )}
      {succeeded && (
        <p role="status">
          Sent to referral review.{' '}
          <a href="/proposals" className="underline">
            Open referral inbox
          </a>
        </p>
      )}
    </>
  );
}
function useReferralSubmission() {
  const { organization } = useAuth();
  const { can } = usePermissions();
  const org = organization?.organization_id;
  const cache = useQueryClient();
  const permitted = !!org && ['trigger_agent', 'read_agent_proposal', 'read_document', 'read_patient'].every(can);
  const mutation = useMutation({
    onSuccess: () => cache.invalidateQueries({ queryKey: ['agent-jobs', org] }),
    mutationFn: async (ids: string[]) =>
      (await clientApi.post(`/api/v1/organizations/${org}/agent/trigger-batch`, { revision_ids: ids })).data,
  });
  return { org, mutation, permitted };
}
